import { clearPendingAuthAction, registerAuthAction, resumePendingAuthAction, runWithAuth } from '../src/auth/authGate.ts';
import { completeEmailSignIn, onAuthStateChanged } from '../src/auth.js';
import { initPaymentForm } from '../src/payment/PaymentForm.js';
import { routeScreen } from './app.flow.js';
import { updatePreview, toggleMobileTab } from './app.preview.js';
import { renderReviewScreen } from './app.review.js';
import { buildSidebarNavigation, calculateAndRenderProgress, goToStep, handleNextButton, isQuestionnaireComplete, updateWizardUI } from './app.wizard.js';
import { SECTIONS } from './questions.js';

const state = { activeScreen: 'sample', activeTab: 'form', answers: {}, childId: null, currentStep: -1, doneNotice: '', entitlement: null, productId: null, questionnaireComplete: false, readOnly: false, reviewCanResend: false, reviewNotice: '', screenIntent: 'auto', user: null };
let elements = {}; let autosaveTimeout = null; let lastAutosaveTime = 0; let handbooksApiPromise = null; let entitlementsApiPromise = null;
const byId = (id) => document.getElementById(id);
const ctx = { state, elements, saveStateToStorage, updatePreview: () => updatePreview({ elements, state }), updateWizardUI: () => updateWizardUI(ctx), renderDashboard: () => void renderDashboard(), renderReviewScreen: (mode) => renderReviewScreen(mode, ctx), routeScreen: () => route(), goToStep: (step) => goToStep(step, ctx) };
const getHandbooksApi = () => (handbooksApiPromise ??= import('../src/api/firestore.handbooks.js'));
const getEntitlementsApi = () => (entitlementsApiPromise ??= import('../src/api/firestore.entitlements.js'));

function saveStateToStorage() {
  localStorage.setItem('gentle_connections_answers', JSON.stringify(state.answers));
  localStorage.setItem('gentle_connections_child_id', state.childId || '');
  localStorage.setItem('gentle_connections_product_id', state.productId || '');
  localStorage.setItem('gentle_connections_step', String(state.currentStep));
  elements.autosaveIndicator.textContent = 'Saving...';
  if (autosaveTimeout) clearTimeout(autosaveTimeout);
  autosaveTimeout = setTimeout(async () => {
    if (!state.childId || !state.productId || Date.now() - lastAutosaveTime < 2000) return;
    try {
      const { saveProductDraft } = await getHandbooksApi();
      await saveProductDraft(state); lastAutosaveTime = Date.now(); elements.autosaveIndicator.textContent = '☁️ Synced to cloud';
    } catch {
      elements.autosaveIndicator.textContent = 'Saved locally';
    }
  }, 2000);
}

function cacheElements() {
  elements = ['screen-sample', 'screen-pay', 'app-workspace', 'mobile-tabs', 'screen-review', 'screen-done', 'welcome-step', 'handbook-form', 'questions-viewport', 'btn-start-wizard', 'btn-wizard-back', 'btn-wizard-next', 'btn-preview-print', 'btn-sample-cta', 'steps-navigation', 'progress-circle', 'progress-text', 'autosave-indicator', 'modal-reset', 'modal-import', 'btn-reset-trigger', 'btn-reset-cancel', 'btn-reset-confirm', 'btn-import-trigger', 'btn-import-cancel', 'btn-import-confirm', 'btn-export-trigger', 'import-file-input', 'import-error', 'tab-form', 'tab-preview', 'rendered-handbook', 'preview-container', 'dashboard-container', 'form-container', 'btn-dashboard', 'payment-heading', 'payment-copy', 'payment-element', 'payment-error', 'btn-pay-submit', 'payment-processing'].reduce((map, id) => ({ ...map, [id.replace(/-([a-z])/g, (_, c) => c.toUpperCase())]: byId(id) }), {});
  ctx.elements = elements;
}

function loadSavedState() {
  try {
    state.answers = JSON.parse(localStorage.getItem('gentle_connections_answers') || '{}');
    state.childId = localStorage.getItem('gentle_connections_child_id') || null;
    state.productId = localStorage.getItem('gentle_connections_product_id') || null;
    state.currentStep = Number(localStorage.getItem('gentle_connections_step') ?? -1);
    if (!Number.isFinite(state.currentStep) || state.currentStep < -1 || state.currentStep > SECTIONS.length) state.currentStep = -1;
  } catch { state.answers = {}; state.childId = null; state.productId = null; state.currentStep = -1; }
}

function route() { state.questionnaireComplete = isQuestionnaireComplete(state); state.readOnly = state.entitlement?.status === 'fulfilled'; return routeScreen(ctx); }

async function refreshEntitlementStatus() {
  if (!state.user || !state.childId || !state.productId) { state.entitlement = null; return route(); }
  try {
    const { getLatestEntitlementByProduct } = await getEntitlementsApi();
    state.entitlement = await getLatestEntitlementByProduct(state.childId, state.productId, state.user.uid);
  } catch { state.entitlement = null; }
  return route();
}

function resetDraft() {
  Object.assign(state, { answers: {}, childId: null, currentStep: -1, doneNotice: '', entitlement: null, productId: null, reviewCanResend: false, reviewNotice: '', screenIntent: 'dashboard' });
  ['gentle_connections_answers', 'gentle_connections_child_id', 'gentle_connections_product_id', 'gentle_connections_step'].forEach((key) => localStorage.removeItem(key));
  buildSidebarNavigation({ state, elements, goToStep: (step) => goToStep(step, ctx) }); route(); hideModal('reset');
}

function showModal(type) {
  if (type === 'import') { elements.importError.style.display = 'none'; elements.importFileInput.value = ''; elements.btnImportConfirm.setAttribute('disabled', 'true'); }
  elements[`modal${type[0].toUpperCase()}${type.slice(1)}`].style.display = 'flex';
}

const hideModal = (type) => { elements[`modal${type[0].toUpperCase()}${type.slice(1)}`].style.display = 'none'; };

function exportState() {
  const payload = { answers: state.answers, app: 'Gentle Connections', childId: state.childId, exportDate: new Date().toISOString(), productId: state.productId, step: state.currentStep, version: '1.0' };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const link = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `${(state.answers.childName || 'Child').replace(/\s+/g, '_')}_handbook_progress.json` });
  document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
}

function importState() {
  const file = elements.importFileInput.files?.[0]; if (!file) return;
  const reader = new FileReader();
  reader.onload = ({ target }) => {
    try {
      const data = JSON.parse(String(target?.result || '{}')); if (data.app !== 'Gentle Connections' || !data.answers) throw new Error('Invalid');
      state.answers = data.answers; state.childId = data.childId || null; state.productId = data.productId || null; state.currentStep = Number(data.step ?? 0); state.screenIntent = state.childId && state.productId ? 'wizard' : 'dashboard';
      saveStateToStorage(); route(); calculateAndRenderProgress(ctx); hideModal('import');
    } catch { elements.importError.style.display = 'block'; }
  };
  reader.readAsText(file);
}

async function loadProductIntoApp(childId, productId, screenIntent = 'wizard') {
  try {
    const { loadProduct } = await getHandbooksApi();
    const product = await loadProduct(childId, productId);
    Object.assign(state, { answers: product?.answers || { childName: product?.childName || '' }, childId, currentStep: product?.currentStep ?? -1, productId, screenIntent });
    saveStateToStorage(); buildSidebarNavigation({ state, elements, goToStep: (step) => goToStep(step, ctx) }); await refreshEntitlementStatus(); calculateAndRenderProgress(ctx); window.scrollTo(0, 0);
  } catch { alert('Failed to load handbook product. Please try again.'); state.screenIntent = 'dashboard'; route(); }
}

const renderDashboard = async () => (await import('../src/components/ProfileDashboard.js')).showProfileDashboard();

async function completePendingMagicLink() {
  try { if (await completeEmailSignIn()) history.replaceState(null, '', window.location.pathname); }
  catch { clearPendingAuthAction(); alert('That sign-in link is invalid or expired. Please request a new one.'); }
}

document.addEventListener('DOMContentLoaded', async () => {
  cacheElements(); loadSavedState(); buildSidebarNavigation({ state, elements, goToStep: (step) => goToStep(step, ctx) }); initPaymentForm(ctx); await completePendingMagicLink();
  registerAuthAction('open-dashboard', async () => { state.screenIntent = 'dashboard'; await route(); });
  registerAuthAction('start-purchase', async () => { state.screenIntent = 'dashboard'; await route(); });
  onAuthStateChanged(async (user) => {
    state.user = user; elements.btnDashboard.style.display = user ? 'inline-flex' : 'none';
    if (!user) { state.childId = null; state.productId = null; state.screenIntent = 'sample'; route(); return; }
    byId('login-modal')?.remove();
    try { const { syncOnSignIn } = await import('../src/api/storage-sync.js'); await syncOnSignIn(); } catch { state.screenIntent = 'dashboard'; }
    if (!state.childId || !state.productId) state.screenIntent = 'dashboard';
    await refreshEntitlementStatus(); await resumePendingAuthAction();
  });
  elements.btnDashboard.addEventListener('click', () => { state.childId = null; state.productId = null; state.screenIntent = 'dashboard'; void route(); });
  elements.btnStartWizard.addEventListener('click', () => goToStep(0, ctx));
  elements.btnWizardBack.addEventListener('click', () => goToStep(state.currentStep - 1, ctx));
  elements.btnWizardNext.addEventListener('click', () => handleNextButton(ctx));
  elements.btnPreviewPrint.addEventListener('click', () => window.open('/sample-handbook.pdf', '_blank', 'noopener'));
  elements.btnSampleCta.addEventListener('click', () => runWithAuth('start-purchase'));
  elements.tabForm.addEventListener('click', () => toggleMobileTab({ elements, state }, 'form'));
  elements.tabPreview.addEventListener('click', () => toggleMobileTab({ elements, state }, 'preview'));
  elements.btnResetTrigger.addEventListener('click', () => showModal('reset'));
  elements.btnResetCancel.addEventListener('click', () => hideModal('reset'));
  elements.btnResetConfirm.addEventListener('click', resetDraft);
  elements.btnImportTrigger.addEventListener('click', () => showModal('import'));
  elements.btnImportCancel.addEventListener('click', () => hideModal('import'));
  elements.btnExportTrigger.addEventListener('click', exportState);
  elements.importFileInput.addEventListener('change', () => elements.btnImportConfirm.toggleAttribute('disabled', !elements.importFileInput.files?.[0]));
  elements.btnImportConfirm.addEventListener('click', importState);
  window.addEventListener('product-open', async (event) => { await loadProductIntoApp(event.detail.childId, event.detail.productId, event.detail.screenIntent); });
  window.addEventListener('dashboard-return', () => runWithAuth('open-dashboard'));
  window.addEventListener('auth-success', () => byId('login-modal')?.remove());
  updatePreview({ state, elements }); route(); window.loadProductIntoApp = loadProductIntoApp; window.resetAppForNewProfile = resetDraft;
});
